import type { ItemBase } from './manual-types';

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

let contador = 0;

/** Identificador estável para itens de listas dinâmicas. */
export function novoId(prefixo = 'item'): string {
  contador += 1;
  const aleatorio = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${prefixo}-${contador}-${aleatorio}`;
}

/** `2026-08-18` → `18-AGO-26`; vazio vira o marcador do modelo. */
export function formatarData(data: string, vazio = 'DD-MMM-AA'): string {
  if (!data) return vazio;
  const [ano, mes, dia] = data.split('-');
  const indice = Number(mes) - 1;
  if (!ano || !dia || indice < 0 || indice > 11) return vazio;
  return `${dia}-${MESES[indice]}-${ano.slice(-2)}`;
}

export function formatarDataExtenso(data: string): string {
  if (!data) return 'N/I';
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** Texto exibido no documento quando o campo não foi informado. */
export function ou(valor: string | null | undefined, alternativa = 'N/I'): string {
  const limpo = (valor ?? '').trim();
  return limpo || alternativa;
}

export function textoPreenchido(valor: string | null | undefined): boolean {
  return Boolean((valor ?? '').trim());
}

/** Itens de lista que possuem ao menos um campo textual preenchido. */
export function itensValidos<T extends ItemBase>(itens: T[] | undefined, campos: (keyof T)[]): T[] {
  if (!itens) return [];
  return itens.filter((item) => campos.some((campo) => textoPreenchido(String(item[campo] ?? ''))));
}

export function fatiar<T>(itens: T[], tamanho: number): T[][] {
  if (tamanho <= 0) return [itens];
  const partes: T[][] = [];
  for (let indice = 0; indice < itens.length; indice += tamanho) {
    partes.push(itens.slice(indice, indice + tamanho));
  }
  return partes.length ? partes : [[]];
}

/**
 * Divide em partes respeitando o máximo, mas equilibrando o tamanho:
 * 13 itens com máximo 12 viram 7 + 6, e não 12 + 1.
 */
export function fatiarEquilibrado<T>(itens: T[], maximo: number): T[][] {
  if (maximo <= 0 || itens.length <= maximo) return [itens];
  const partes = Math.ceil(itens.length / maximo);
  return fatiar(itens, Math.ceil(itens.length / partes));
}

/** Estimativa de linhas ocupadas por um texto na largura útil da página A4. */
export function linhasDeTexto(texto: string, caracteresPorLinha = 96): number {
  const limpo = (texto ?? '').trim();
  if (!limpo) return 0;
  return limpo
    .split('\n')
    .reduce((total, linha) => total + Math.max(1, Math.ceil(linha.length / caracteresPorLinha)), 0);
}

/** Quebra um texto multilinha em parágrafos aproveitáveis pelo documento. */
export function paragrafos(texto: string): string[] {
  return (texto ?? '')
    .split(/\n{1,}/)
    .map((parte) => parte.trim())
    .filter(Boolean);
}

export function baixarArquivo(nome: string, conteudo: string, tipo = 'application/json') {
  const blob = new Blob([conteudo], { type: `${tipo};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function normalizarNomeArquivo(valor: string, alternativa = 'manual-operacao'): string {
  const base = (valor || alternativa)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return base || alternativa;
}
